import { useState } from 'react';
import { Button } from '../Button.tsx';
import { bytes as fmtBytes, duration, midEllipsis, utcTime, NO_VALUE } from '../format.ts';
import { backoffMs, retriesFor, stateWord, type FaultClass } from './fault.ts';
import { useOnline } from './read.ts';
import { endpointCounts, useAttempts, useSources, type Source } from './sources.ts';
import styles from './strip.module.css';

/* ==========================================================================
   SUPPLY — the diagnostics, as a 24px line at the foot of the surface, and a
   196px ledger behind it.

   WHY IT IS A LINE FIRST.

   The band used to open to its full height the moment anything failed, and it
   billed the route ~216px of height for as long as that lasted. On a partial
   outage — three sources out of eight, the fleet register among the five that
   are fine — that is the console spending a fifth of the viewport on a ledger
   about the failure and taking it out of the thirteen robots that need
   attention. It also squeezed the route's own grid hard enough that a KPI
   band with a fixed content height overflowed the row it was given.

   A diagnosis is worth its pixels while you are diagnosing. So the default is
   ONE 24px line carrying the four facts a triaging operator reads first — how
   many sources are out, whether this browser is even on a network, which host
   it is talking to, and how many of its reads have landed — and the ledger is
   one keystroke behind it. It opens by itself only in the one case where the
   route has nothing to lose: nothing has landed this session and nothing is
   reaching the API, so the 216px was going to be charcoal anyway.

   The line's figures are the ones the banner deliberately does not carry: the
   banner says which sources stopped and how long they have been silent, this
   says what the BROWSER can see. Between them nothing is printed twice.

   Three questions, in the order an outage is actually triaged, each in its
   own column, none of them able to push the route off the screen:

     THE LINE        is it me or is it them — the browser's own account of
                     itself, containing not one figure that came from the API.
                     Always visible, because it is the first question
     SOURCES         which endpoints stopped, how often each is read, and the
                     absolute Zulu stamp of the last time each one worked
     RETRY POLICY    the ladder the query client will actually run, off the
                     same module it installs
     READ ATTEMPTS   dead or flapping — every /api request this document made,
                     at its true timestamp, off `PerformanceResourceTiming`

   There is no verb in here. The one recovery lives in the banner at the top
   of the surface, because a screen that offers Retry in six places has told
   the operator that it does not know which one is the real one.
   ========================================================================== */

/* The operator's own choice, for as long as the tab is open. Not React state
   — the strip unmounts on every route change and on every recovery, and an
   operator who opened the ledger to watch a flapping link should not have to
   open it again each time the link comes back for four seconds. */
let opened: boolean | null = null;

/* How stale a round-trip figure may be before it stops being one. Twenty
   seconds is four times the fastest poll on this console, so a healthy screen
   never blinks it, and a stopped one drops it within one missed cycle. */
const FRESH_MS = 20_000;

export function SupplyStrip({
  surface,
  /** Open on its own — only where the route has nothing to lose. */
  cold = false,
}: {
  surface?: string | undefined;
  cold?: boolean | undefined;
}) {
  const sources = useSources();
  const attempts = useAttempts();
  /* `attempts.to` is the window's own upper edge, sampled on the same 1 Hz
     tick that ages every row in this strip — so the round trip's freshness is
     judged against exactly the clock the ledger beside it is drawn from. */
  const now = attempts.to;
  const [choice, setChoice] = useState<boolean | null>(() => opened);
  const failing = sources.filter((s) => s.supply);
  /* One definition, shared with the banner's `No network` verdict — the strip
     reporting `Network offline` under a banner that says `Link down` is the
     same event described twice with two different implications. */
  const online = useOnline();
  const span = Math.max(1, attempts.to - attempts.from);
  const open = choice ?? cold;
  /* The same fraction the banner prints — endpoints, not query instances. Two
     numbers for one fact, 900px apart, is how an operator stops trusting
     either. */
  const counts = endpointCounts(sources);

  const lanes = [...attempts.byPath.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  /* One row per DISTINCT fault class, not per source: eight endpoints failing
     on the same dead link are one ladder, and printing it eight times is the
     strip repeating itself. The figures come out of `fault.ts`, which is the
     module `queryClient.ts` installs — so the schedule on screen is the
     schedule the console will actually run. */
  const classes = [...new Set(failing.map((s) => s.cls))].filter(
    (c): c is FaultClass => c !== null,
  );
  /* The cadence the console falls back to once the ladder is spent — the
     fastest declared poll among the sources that are actually failing, read
     off the observers that declared it. */
  const poll = failing.reduce<number | null>(
    (best, s) =>
      s.everyMs === null || s.cls === 'auth' || s.cls === 'forbidden'
        ? best
        : best === null
          ? s.everyMs
          : Math.min(best, s.everyMs),
    null,
  );

  const summary: { label: string; value: string; tone?: 'error' | 'ok' | undefined }[] = [
    { label: 'Network', value: online ? 'online' : 'offline', tone: online ? undefined : 'error' },
    /* NO `Host` FIELD. It was here, and it printed `127.0.0.1:5195` twenty-two
       pixels above the shell's own status bar printing `127.0.0.1:5195` — one
       string, twice, in two typefaces, in the one region of the screen an
       operator is scanning for the fact that differs. The shell states the
       host on every route, outage or not; this line states what the BROWSER
       can see about reaching it, which is the half nothing else says. */
    {
      label: 'Reads',
      value: `${attempts.ok} ok · ${attempts.failed} failed`,
      tone: attempts.failed > 0 && attempts.ok === 0 ? 'error' : undefined,
    },
    { label: 'Carried', value: fmtBytes(attempts.bytes) },
    /* A LATENCY IS ONLY A LATENCY WHILE IT IS STILL TRUE.

       The window behind these figures is two minutes wide, which is right for
       the counts — an operator wants to know how many reads have been lost —
       and wrong for the round trip: ninety seconds into an outage the median
       is computed entirely from reads that landed before it started, and it
       prints a healthy `40 ms` beside `5 of 7 sources failing`. THE BAR §11.10
       is explicit that a latency figure nobody can still stand behind is the
       single worst number this product can render, so it goes to `—` as soon
       as nothing has landed for a poll's worth of time. The counts keep the
       full window; only the figure an operator would ACT on is gated. */
    {
      label: 'Round trip',
      value:
        attempts.p50 === null || attempts.okAt === null || now - attempts.okAt > FRESH_MS
          ? NO_VALUE
          : `${attempts.p50} ms`,
    },
  ];

  return (
    <aside
      className={styles.strip}
      data-open={open}
      /* Opened by the surface rather than by the operator — so the ledger is
         capped one row shorter than the form they asked for, because this
         height is being taken out of a route they did not choose to shrink.
         See strip.module.css. */
      data-cold={cold}
      aria-label={surface === undefined ? 'Supply diagnostics' : `Supply diagnostics — ${surface}`}
    >
      {/* THE LINE. Uppercase eyebrow, the count, then the browser's own account
          of itself — not one figure of which came from the API whose failure is
          being diagnosed. */}
      <div className={styles.summary}>
        <span className={styles.summaryEyebrow}>Supply</span>
        <span className={styles.summaryCount} data-tone={counts.failing > 0 ? 'error' : 'none'}>
          {counts.failing} of {counts.total} sources failing
        </span>
        <span className={styles.summarySep} aria-hidden="true" />
        {summary.map((f) => (
          <span className={styles.summaryField} key={f.label}>
            <span className={styles.summaryLabel}>{f.label}</span>
            <span className={styles.summaryValue} data-tone={f.tone ?? 'none'}>
              {f.value}
            </span>
          </span>
        ))}
        <span className={styles.spacer} />
        {/* Named, not a bare chevron: an icon-only disclosure at the foot of a
            screen is a control nobody finds during the four minutes they need
            it. */}
        <Button
          size="sm"
          variant="ghost"
          iconEnd={open ? 'chevron-down' : 'chevron-up'}
          aria-expanded={open}
          title={open ? 'Hide the supply ledger' : 'Show the supply ledger — sources, retry ladder, read attempts'}
          onClick={() => {
            opened = !open;
            setChoice(!open);
          }}
        >
          Ledger
        </Button>
      </div>

      {open && (
      <div className={styles.cols}>
      <section className={`${styles.col} ${styles.colSources}`}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>Sources</span>
          <span className={styles.spacer} />
          <span className={styles.aside} data-tone={counts.failing > 0 ? 'error' : 'none'}>
            {counts.failing} of {counts.total} failing
          </span>
        </div>
        {/* A console reading a dozen sources overflows this column, and a
            scroller a keyboard user cannot reach is a list they cannot read —
            so it is a named, focusable region. */}
        <div className={styles.scroll} tabIndex={0} role="group" aria-label="Mounted sources">
          {sources.length === 0 ? (
            <p className={styles.blank}>No sources mounted on this surface.</p>
          ) : (
            <div role="table" aria-label="Console sources">
              <div className={styles.ledgerHead} role="row">
                <span aria-hidden="true" />
                <span role="columnheader">Source</span>
                <span role="columnheader">Scope</span>
                <span role="columnheader" className={styles.every}>
                  Every
                </span>
                <span role="columnheader">State</span>
                <span role="columnheader" className={styles.stamp}>
                  Last good
                </span>
              </div>
              {sources.map((s) => (
                <SourceRow key={s.id} source={s} />
              ))}
            </div>
          )}
        </div>
      </section>

      {classes.length > 0 && (
        <section className={`${styles.col} ${styles.colPolicy}`}>
          <div className={styles.head}>
            <span className={styles.eyebrow}>Retry policy</span>
            <span className={styles.spacer} />
            {/* What the ladder is FOR, not which file it lives in. An operator
                triaging an outage has no use for a source filename, and a
                console that prints one has stopped talking to them. */}
            <span className={styles.aside}>per fault class</span>
          </div>
          <div className={styles.scroll}>
            {classes.map((c) => (
              <PolicyRow key={c} cls={c} />
            ))}
            {/* What happens when the ladder is spent: the surface's own poll
                picks the source back up. Printing the ladder without this is
                how a console implies it has given up when it has not. */}
            <div className={styles.kv}>
              <span className={styles.policyCls}>then</span>
              <span className={styles.kvValue}>
                {poll === null ? 'no further attempt' : `every ${duration(poll)}`}
              </span>
            </div>
          </div>
        </section>
      )}

      <section className={`${styles.col} ${styles.colAttempts}`}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>Read attempts</span>
          <span className={styles.spacer} />
          <span className={styles.aside}>
            {attempts.supported ? `last ${duration(span)}` : 'unsupported'}
          </span>
        </div>
        {!attempts.supported ? (
          <p className={styles.blank}>
            This browser does not report resource timings — no attempts to draw.
          </p>
        ) : lanes.length === 0 ? (
          <p className={styles.blank}>No /api requests in the window.</p>
        ) : (
          <>
            <div className={styles.scroll}>
              {lanes.map(([path, marks]) => (
                <Lane key={path} path={path} marks={marks} from={attempts.from} span={span} />
              ))}
            </div>
            {/* Relative, labelled at both ends: this axis is two minutes wide
                and always ends at now, so an absolute stamp on it would be a
                second clock to reconcile with the header's. The absolute
                stamps are in the ledger, on the reads that landed. */}
            <div className={styles.ruler} aria-hidden="true">
              <span>−{duration(span)}</span>
              <span className={styles.spacer} />
              <span>now</span>
            </div>
          </>
        )}
      </section>
      </div>
      )}
    </aside>
  );
}

/**
 * One mounted source. It carries the reason when it stopped and the absolute
 * Zulu stamp of its last good read when it has not — the two things a
 * triaging operator reads off a ledger, and never both at once.
 */
function SourceRow({ source }: { source: Source }) {
  const failing = source.state === 'failing';
  /* A 404 or a 403 is not a break in supply — the console asked for something
     and got a complete answer — so it is drawn as an answer and is not in the
     eyebrow's count. */
  const state = failing ? (source.supply ? 'failing' : 'refused') : source.state;
  const value = failing
    ? source.cls === null
      ? 'read failed'
      : stateWord(source.cls)
    : source.lastGoodIso === null
      ? source.fetching
        ? 'reading…'
        : 'no read yet'
      : 'answering';

  return (
    <div
      className={styles.source}
      data-state={state}
      role="row"
      title={`${source.label}${source.scope === null ? '' : ` · ${source.scope}`} — ${
        failing ? `${source.reason ?? 'read failed'}, ${source.runs} in a row` : value
      }`}
    >
      <span className={styles.tick} aria-hidden="true" />
      <span className={styles.name} role="cell">
        {source.label}
      </span>
      <span className={styles.path} role="cell">
        {source.scope === null ? NO_VALUE : midEllipsis(source.scope, 16)}
      </span>
      <span className={styles.every} role="cell">
        {source.everyMs === null ? 'once' : duration(source.everyMs)}
      </span>
      <span className={styles.state} role="cell">
        {value}
        {/* Same words as the panel's own fault line — `4×` here over `4 lost`
            in a panel 400px above is one figure printed two ways. */}
        {failing && source.runs > 1 && <span className={styles.runs}> · {source.runs} lost</span>}
      </span>
      <span className={styles.stamp} role="cell">
        {source.lastGoodIso === null ? NO_VALUE : utcTime(source.lastGoodIso)}
      </span>
    </div>
  );
}

/** The retry ladder for one class, off the functions the query client runs. */
function PolicyRow({ cls }: { cls: FaultClass }) {
  const budget = retriesFor(cls, null);
  return (
    <div className={styles.kv}>
      <span className={styles.policyCls}>{cls}</span>
      <span className={styles.kvValue}>
        {budget === 0
          ? 'no retry'
          : Array.from({ length: budget }, (_, i) => `${backoffMs(i, cls) / 1000}s`).join(' · ')}
      </span>
    </div>
  );
}

function Lane({
  path,
  marks,
  from,
  span,
}: {
  path: string;
  marks: readonly { t: number; ok: boolean }[];
  from: number;
  span: number;
}) {
  const ok = marks.filter((m) => m.ok).length;
  const failed = marks.length - ok;
  return (
    <div className={styles.lane}>
      <span className={styles.laneName} title={path}>
        {midEllipsis(path.replace(/^\/api\//, ''), 15)}
      </span>
      <span
        className={styles.laneTrack}
        role="img"
        aria-label={`${path}: ${ok} of ${marks.length} reads answered${
          failed > 0 && ok > 0 ? ', losing some but not all' : failed > 0 ? ', none answered' : ''
        }`}
      >
        {marks.map((m, i) => (
          <span
            key={`${m.t}:${i}`}
            className={styles.mark}
            data-ok={m.ok}
            style={{ left: `${((m.t - from) / span) * 100}%` }}
          />
        ))}
      </span>
      {/* A zero is a reading here: `0/7` on a source polling every five
          seconds is the whole story. */}
      <span className={styles.laneTally}>
        <span data-zero={ok === 0}>{ok}</span>/{marks.length}
      </span>
    </div>
  );
}
